import React, { useState, useRef, useCallback, useEffect } from 'react';
import { InsufficientCreditsModal } from '../components/modals/InsufficientCreditsModal';
import { useSearchParams } from 'react-router-dom';
import { actors, Actor } from '../data/actors';
import ActorPickerModal from '../components/ActorPickerModal';
import { loadDraftImage } from '../utils/draftMedia';
import { updateAsset } from '../services/assets';
import { useAuth } from '../contexts/AuthContext';
import InteractionGuard from '../components/guards/InteractionGuard';
import { uploadImageToStorage } from '../services/storage';
import { useVideoGeneration } from '../hooks/useVideoGeneration';
import { useGenerationProgression } from '../hooks/useGenerationProgression';
import GenerationProcessingModal from '../components/modals/GenerationProcessingModal';
import GenerationResultModal from '../components/modals/GenerationResultModal';
import { useUser } from '../hooks/useApi';

/**
 * Helper function to save pending image and prompt to sessionStorage
 * Used when user is not authenticated - persists data across auth flow
 */
const savePendingImageData = (file: File, promptText: string) => {
  try {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      const imageData = {
        name: file.name,
        type: file.type,
        size: file.size,
        base64: base64
      };
      sessionStorage.setItem('pending_image_to_video_file', JSON.stringify(imageData));
    };
    reader.readAsDataURL(file);
    
    // Save prompt separately
    if (promptText.trim()) {
      sessionStorage.setItem('pending_image_to_video_prompt', promptText.trim());
    }
  } catch (error) {
    console.error('❌ [HomePage] Failed to save pending image:', error);
  }
};

/**
 * Helper function to restore pending image and prompt from sessionStorage
 * Called after user authenticates
 */
const restorePendingImageData = (): { file: File; prompt: string; preview: string } | null => {
  try {
    const savedImage = sessionStorage.getItem('pending_image_to_video_file');
    const savedPrompt = sessionStorage.getItem('pending_image_to_video_prompt');
    
    if (!savedImage) {
      return null;
    }
    
    const imageData = JSON.parse(savedImage);
    
    // Convert base64 back to File object
    const base64Data = imageData.base64.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: imageData.type });
    const file = new File([blob], imageData.name, { type: imageData.type });
    
    return {
      file,
      prompt: savedPrompt || '',
      preview: imageData.base64
    };
  } catch (error) {
    console.error('❌ [HomePage] Failed to restore pending image:', error);
    return null;
  }
};

/**
 * Helper function to clear pending image data from sessionStorage
 * Called after successful generation or when user manually resets
 */
const clearPendingImageData = () => {
  sessionStorage.removeItem('pending_image_to_video_file');
  sessionStorage.removeItem('pending_image_to_video_prompt');
};

export default function HomePage() {
  const [searchParams] = useSearchParams();
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedActor, setSelectedActor] = useState<Actor | null>(null);
  const [prompt, setPrompt] = useState('');
  const [isActorModalOpen, setIsActorModalOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [imageAssetId, setImageAssetId] = useState<string | null>(null); // Track uploaded image asset
  const [showSignUpModal, setShowSignUpModal] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null); // Track upload validation errors
  const [insufficientCredits, setInsufficientCredits] = useState<{
    current_credits: number;
    required_credits: number;
    message: string;
  } | null>(null);
  const { isAuthenticated, user } = useAuth();
  const { user: userData } = useUser(); // Get user data including subscription status
  const pendingGenerateRef = useRef(false); // Track if generation is pending after sign up
  const shouldStartGenerationRef = useRef(false); // Track if we should start generation after upload completes

  // Clear pending generation flag on mount to prevent accidental auto-generation
  useEffect(() => {
    pendingGenerateRef.current = false;
  }, []);

  // Use progression tracking hook
  const {
    progression,
    setStage,
    updateStageProgress,
    setError: setProgressionError,
    reset: resetProgression,
  } = useGenerationProgression({
    onComplete: () => {},
    onError: (err) => {
      // Only log if it's a progression tracking error, not a video generation error
      if (err && !err.includes('Failed to fetch') && !err.includes('Video generation')) {
        console.error('❌ [HomePage] Progression tracking error:', err);
      }
    },
  });

  // Use video generation hook
  // Follow same pattern as Lander1Page - use custom prompt from user input
  const {
    status,
    progress,
    videoUrl,
    error,
    startGeneration: startVideoGeneration,
    reset: resetVideoGeneration,
  } = useVideoGeneration({
    assetId: imageAssetId || '',
    prompt: prompt.trim(), // Use trimmed prompt - same as Lander1Page but with custom prompt
    userEmail: user?.email && !user.is_anonymous ? user.email : undefined,
    userData: userData, // Pass user data for watermarking logic
    onComplete: (url) => {
      setShowResultModal(true);
      
      // Clear image-related data to ensure user uploads fresh image next time
      sessionStorage.removeItem('uploaded_image_url');
      sessionStorage.removeItem('uploaded_image_path');
      sessionStorage.removeItem('uploaded_asset_id');
      clearPendingImageData();
    },
    onError: (err) => {
      console.error('❌ [HomePage] Video generation error via hook:', err);
      
      // Check if this is an insufficient credits error
      if (err && typeof err === 'object' && 'type' in err && (err as any).type === 'insufficient_credits') {
        // Reset progression to close the processing modal
        resetProgression();
        // Reset video generation to clear the error state
        resetVideoGeneration();
        
        setInsufficientCredits({
          current_credits: (err as any).current_credits ?? 0,
          required_credits: (err as any).required_credits ?? 100,
          message: (err as any).message || 'Insufficient credits to generate video',
        });
        return;
      }
      
      // For other errors, also reset progression to close the processing modal
      resetProgression();
    },
    // Connect progression tracking
    // Wrap in try-catch to prevent progression errors from breaking generation
    onStageChange: (stage, message) => {
      try {
        setStage(stage, 0, message);
      } catch (error) {
        console.error('❌ [HomePage] Progression tracking error (non-fatal):', error);
        // Don't throw - let generation continue
      }
    },
    onStageProgress: (stage, progress) => {
      try {
        updateStageProgress(progress);
      } catch (error) {
        console.error('❌ [HomePage] Progression progress error (non-fatal):', error);
        // Don't throw - let generation continue
      }
    },
  });

  // Watch for errors and show insufficient credits modal if needed
  useEffect(() => {
    if (error && error.includes('insufficient_credits')) {
      // Parse the error message to extract credit information
      try {
        // The error might contain JSON with the credit info
        const match = error.match(/\{.*"type":"insufficient_credits".*\}/);
        if (match) {
          const errorData = JSON.parse(match[0]);
          resetProgression();
          resetVideoGeneration();
          
          setInsufficientCredits({
            current_credits: errorData.current_credits ?? 0,
            required_credits: errorData.required_credits ?? 100,
            message: errorData.message || 'Insufficient credits to generate video',
          });
        }
      } catch (e) {
        console.error('Failed to parse insufficient credits error:', e);
        // Fallback: show modal with default values
        resetProgression();
        resetVideoGeneration();
        setInsufficientCredits({
          current_credits: 0,
          required_credits: 100,
          message: 'You need 100 credits to generate a video. You currently have 0 credits.',
        });
      }
    }
  }, [error, resetProgression, resetVideoGeneration]);

  // Load draft image and prompt on mount AND when returning after sign-up
  // This runs on mount and whenever authentication state changes
  useEffect(() => {
    // FIRST: Restore prompt ONLY if it's empty (don't overwrite user input!)
    const pendingPrompt = sessionStorage.getItem('pending_prompt');
    if (pendingPrompt && !prompt.trim()) {
      // Only restore if prompt is currently empty - don't overwrite what user is typing!
      setPrompt(pendingPrompt);
    }
    
    // NOTE: Do NOT restore images from draft or sessionStorage on mount
    // Images should NEVER be preserved - user must always upload fresh image
    // This prevents confusion and ensures clean state for each generation
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isAuthenticated, user?.is_anonymous]); // Run when auth state changes too

  // Helper function to get image dimensions
  const getImageDimensions = (file: File): Promise<{ width: number; height: number }> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      
      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve({ width: img.width, height: img.height });
      };
      
      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load image'));
      };
      
      img.src = url;
    });
  };

  const validateImageDimensions = async (file: File): Promise<{ valid: boolean; error?: string }> => {
    const minWidth = 240;
    const minHeight = 240;
    
    try {
      const dimensions = await getImageDimensions(file);
      if (dimensions.width < minWidth || dimensions.height < minHeight) {
        return { 
          valid: false, 
          error: `Image too small. Minimum size: ${minWidth}x${minHeight} pixels. Your image: ${dimensions.width}x${dimensions.height} pixels.` 
        };
      }
      return { valid: true };
    } catch (error) {
      console.error('Failed to read image dimensions:', error);
      return { valid: false, error: 'Failed to read image. Please try a different file.' };
    }
  };

  const handleImageSelect = useCallback(async (file: File) => {
    if (file && file.type.startsWith('image/')) {
      // Validate dimensions
      const validation = await validateImageDimensions(file);
      if (!validation.valid) {
        setUploadError(validation.error || 'Invalid image');
        return;
      }
      
      // Clear any previous errors
      setUploadError(null);
      
      setSelectedImage(file);
      setSelectedActor(null); // Clear actor when image is selected
      
      // Create preview ONLY - no upload yet
      // Upload will happen when user clicks "Generate Video" button
      const reader = new FileReader();
      reader.onloadend = () => {
        const previewUrl = reader.result as string;
        setImagePreview(previewUrl);
      };
      reader.readAsDataURL(file);
    }
  }, []); // No dependencies - just sets state

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      handleImageSelect(file);
    }
  };

  const handleActorSelect = (actor: Actor) => {
    setSelectedActor(actor);
    setSelectedImage(null); // Clear image when actor is selected
    setImagePreview(null);
  };

  // Can generate if we have an image (File, preview URL, or asset ID) AND a valid prompt
  const canGenerate = (selectedImage || imagePreview || imageAssetId) && prompt.trim().length >= 5;

  const handleGenerate = async () => {
    if (!canGenerate) {
      return;
    }

    // If not authenticated (including anonymous users), show sign up modal
    if (!isAuthenticated) {
      // Save image and prompt to sessionStorage before showing auth modal
      if (selectedImage && prompt.trim()) {
        savePendingImageData(selectedImage, prompt);
      }
      
      setShowSignUpModal(true);
      pendingGenerateRef.current = true; // Mark that generation should happen after sign up
      return;
    }

    // Proceed with generation
    try {
      await startGeneration();
    } catch (error) {
      console.error('❌ [HomePage] Error in handleGenerate:', error);
      
      // Check if this is an insufficient credits error
      if (error && typeof error === 'object' && 'type' in error && error.type === 'insufficient_credits') {
        // Reset progression to close the processing modal
        resetProgression();
        // Reset video generation to clear the error state
        resetVideoGeneration();
        
        setInsufficientCredits({
          current_credits: (error as any).current_credits ?? 0,
          required_credits: (error as any).required_credits ?? 100,
          message: (error as any).message || 'Insufficient credits to generate video',
        });
        return;
      }
      
      // For other errors, also reset progression to close the processing modal
      resetProgression();
    }
  };

  const startGeneration = async () => {
    // VALIDATION: Ensure we have prompt
    if (!prompt.trim() || prompt.trim().length < 5) {
      console.error('❌ [HomePage] Prompt is required and must be at least 5 characters');
      return;
    }

    // NEW: Upload image if not already uploaded
    if (!imageAssetId && selectedImage) {
      // NOW show progression for upload
      try {
        setStage('uploading_image', 0, 'Uploading image...');
      } catch (error) {
        console.error('❌ [HomePage] Progression tracking error (non-fatal):', error);
      }
      
      try {
        // Simulate upload progress
        try {
          updateStageProgress(50);
        } catch (error) {
          console.error('❌ [HomePage] Progression progress error (non-fatal):', error);
        }
        
        const uploadResult = await uploadImageToStorage(selectedImage, undefined, prompt.trim());
        
        // Track storage stages
        try {
          setStage('storing_image_s3', 100, 'Image stored in S3');
          setTimeout(() => {
            try {
              setStage('storing_image_db', 100, 'Image record saved');
            } catch (error) {
              console.error('❌ [HomePage] Progression storage error (non-fatal):', error);
            }
          }, 300);
        } catch (error) {
          console.error('❌ [HomePage] Progression tracking error (non-fatal):', error);
        }
        
        // Store uploaded asset ID for use in generation
        const uploadedAssetId = uploadResult.asset.id;
        
        // CRITICAL: Set flag BEFORE setting state to ensure useEffect sees it
        shouldStartGenerationRef.current = true;
        
        // Store asset ID and URL in state (this will trigger re-render and useEffect)
        setImageAssetId(uploadedAssetId);
        setImagePreview(uploadResult.url);
        
        // Store in sessionStorage
        sessionStorage.setItem('uploaded_image_url', uploadResult.url);
        sessionStorage.setItem('uploaded_image_path', uploadResult.path);
        sessionStorage.setItem('uploaded_asset_id', uploadedAssetId);
        
        // Exit here - useEffect will handle the rest
        return; // Exit after successful upload
        
      } catch (error) {
        console.error('❌ [HomePage] Upload failed:', error);
        setProgressionError(error instanceof Error ? error.message : 'Upload failed');
        return; // Stop generation if upload fails
      }
    } else if (!imageAssetId) {
      console.error('❌ [HomePage] No image asset ID and no selected image to upload');
      return;
    }

    // Start progression tracking for video generation
    // Image upload stages are done, start at prompt enhancement
    // Follow same pattern as Lander1Page
    if (progression.currentStage === 'idle' || progression.currentStage === 'storing_image_db') {
      try {
        setStage('enhancing_prompt', 0, 'Preparing to enhance prompt...');
      } catch (error) {
        console.error('❌ [HomePage] Progression tracking error (non-fatal):', error);
      }
    }

    try {
      // Save prompt to image asset for full persistence
      // This is important because the prompt enhancer expects the prompt in the asset record
      if (imageAssetId && prompt.trim()) {
        try {
          await updateAsset(imageAssetId, {
            prompt: prompt.trim(),
          });
          sessionStorage.removeItem('pending_prompt');
        } catch (error) {
          console.error('❌ [HomePage] Failed to update image asset with prompt:', error);
          // Don't block generation - the prompt is also sent in the request body
        }
      }

      // Use the hook for generation and polling
      await startVideoGeneration();
    } catch (err) {
      console.error('❌ [HomePage] Generation error:', err);
      setProgressionError(err instanceof Error ? err.message : 'Generation failed');
    }
  };

  const handleReset = () => {
    setSelectedImage(null);
    setImagePreview(null);
    setSelectedActor(null);
    setImageAssetId(null);
    setPrompt('');
    resetVideoGeneration();
    resetProgression();
    
    // Clear all sessionStorage data
    sessionStorage.removeItem('uploaded_image_url');
    sessionStorage.removeItem('uploaded_image_path');
    sessionStorage.removeItem('uploaded_asset_id');
    sessionStorage.removeItem('pending_prompt');
    clearPendingImageData();
  };

  // Handle result modal close
  const handleResultModalClose = () => {
    setShowResultModal(false);
    
    // Reset ALL state including image to force user to upload fresh image
    setSelectedImage(null);
    setImagePreview(null);
    setImageAssetId(null);
    setPrompt('');
    resetVideoGeneration();
    resetProgression();
    
    // Clear all image-related sessionStorage
    sessionStorage.removeItem('uploaded_image_url');
    sessionStorage.removeItem('uploaded_image_path');
    sessionStorage.removeItem('uploaded_asset_id');
    clearPendingImageData();
  };

  // Restore pending image and prompt after authentication
  // This handles the case where user uploaded image + entered prompt, then logged in
  // User will need to click Generate button again after login
  useEffect(() => {
    if (isAuthenticated && pendingGenerateRef.current) {
      const restored = restorePendingImageData();
      
      if (restored) {
        // Restore image file and preview
        setSelectedImage(restored.file);
        setImagePreview(restored.preview);
        
        // Restore prompt
        if (restored.prompt && !prompt.trim()) {
          setPrompt(restored.prompt);
        }
        
        // Clear the pending data from sessionStorage
        clearPendingImageData();
        
        // Clear the pending flag - user can now click Generate button
        pendingGenerateRef.current = false;
      } else {
        pendingGenerateRef.current = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Trigger generation after upload completes and imageAssetId state updates
  useEffect(() => {
    if (shouldStartGenerationRef.current && imageAssetId && prompt.trim()) {
      // Clear the flag
      shouldStartGenerationRef.current = false;
      
      // Start generation in the next tick to ensure all state has propagated
      setTimeout(async () => {
        try {
          // Start progression tracking
          if (progression.currentStage === 'idle' || progression.currentStage === 'storing_image_db') {
            try {
              setStage('enhancing_prompt', 0, 'Preparing to enhance prompt...');
            } catch (error) {
              console.error('❌ [HomePage] Progression tracking error (non-fatal):', error);
            }
          }

          // Save prompt to asset (already done in upload, but double-check)
          if (imageAssetId && prompt.trim()) {
            try {
              await updateAsset(imageAssetId, {
                prompt: prompt.trim(),
              });
              sessionStorage.removeItem('pending_prompt');
            } catch (error) {
              console.error('❌ [HomePage] Failed to update asset:', error);
              // Continue anyway
            }
          }

          // Now call the generation hook
          await startVideoGeneration();
        } catch (err) {
          console.error('❌ [HomePage] Generation error in useEffect:', err);
          setProgressionError(err instanceof Error ? err.message : 'Generation failed');
        }
      }, 100);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageAssetId, prompt]);

  // Show stacked actor thumbnails preview
  const renderActorPreview = () => {
    if (!selectedActor) {
      // Show default stacked preview
      return (
        <div className="flex -space-x-4">
          {actors.slice(0, 3).map((actor, idx) => (
            <div
              key={actor.id}
              className="w-16 h-16 rounded-lg border-2 border-white/20 overflow-hidden bg-white/10"
              style={{ zIndex: 3 - idx }}
            >
              <img
                src={actor.thumbnail}
                alt={actor.name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="64" height="64"%3E%3Crect fill="%23333" width="64" height="64"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="10" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EActor%3C/text%3E%3C/svg%3E';
                }}
              />
            </div>
          ))}
        </div>
      );
    }

    // Show selected actor
    return (
      <div className="flex flex-col items-center">
        <div className="w-20 h-20 rounded-lg border-2 border-white overflow-hidden bg-white/10 mb-2">
          <img
            src={selectedActor.thumbnail}
            alt={selectedActor.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="80" height="80"%3E%3Crect fill="%23333" width="80" height="80"/%3E%3Ctext fill="%23999" font-family="sans-serif" font-size="12" x="50%25" y="50%25" text-anchor="middle" dy=".3em"%3EActor%3C/text%3E%3C/svg%3E';
            }}
          />
        </div>
        <p className="text-white text-sm font-medium">{selectedActor.name}</p>
      </div>
    );
  };

  // Handle authentication after sign up - continue with generation if pending
  // NOTE: This should only trigger if user explicitly clicked Generate button before auth
  useEffect(() => {
    if (isAuthenticated && pendingGenerateRef.current) {
      // Double-check that we have valid generation requirements before starting
      const hasImage = selectedImage || imagePreview || imageAssetId;
      const hasValidPrompt = prompt.trim().length >= 5;
      
      if (hasImage && hasValidPrompt) {
        pendingGenerateRef.current = false;
        setShowSignUpModal(false);
        // Start generation after authentication
        startGeneration();
      } else {
        // Clear the flag if requirements aren't met
        pendingGenerateRef.current = false;
        setShowSignUpModal(false);
      }
    }
  }, [isAuthenticated, selectedImage, imagePreview, imageAssetId, prompt]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {/* Sign Up Modal - shows when anonymous user tries to generate */}
      {showSignUpModal && (
        <InteractionGuard
          onAuthenticated={() => {
            setShowSignUpModal(false);
            // Generation will start automatically via useEffect above
          }}
          onCancel={() => {
            setShowSignUpModal(false);
            pendingGenerateRef.current = false;
          }}
          actionName="generate your video"
        />
      )}

      <div className="min-h-full bg-bg px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <div className="max-w-4xl mx-auto space-y-8">
        {/* Header and Subheader */}
        <div className="text-center space-y-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white">
            Generate Anything
          </h1>
          <p className="text-lg md:text-xl text-white/70">
            Upload any image and make it come to life 😉
          </p>
        </div>

        {/* References Section (no heading) */}
        <div>
          {/* Upload Image Card - Keep existing component exactly as-is, just repositioned */}
          <div className="w-full">
            <div
              className={`border-2 border-dashed rounded-xl p-4 md:p-8 cursor-pointer transition-colors min-h-[180px] md:min-h-[220px] flex flex-col ${
                isDragging
                  ? 'border-white bg-white/5'
                  : 'border-white/30 hover:border-white/50 bg-white/5'
              }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileInputChange}
                className="hidden"
              />
              {imagePreview ? (
                <div className="flex flex-col items-center flex-1 justify-center">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full max-w-md h-auto max-h-64 md:max-h-96 object-contain rounded-lg mb-2 md:mb-4"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedImage(null);
                      setImagePreview(null);
                      setImageAssetId(null);
                      // Reset file input so the same file can be selected again
                      if (fileInputRef.current) {
                        fileInputRef.current.value = '';
                      }
                    }}
                    className="text-white/70 hover:text-white text-xs md:text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex flex-col items-center flex-1 justify-center">
                  <p className="text-white text-base md:text-lg font-medium mb-2 md:mb-4">Upload Image</p>
                  <div className="w-16 h-16 md:w-24 md:h-24 mb-2 md:mb-4 flex items-center justify-center">
                    <svg
                      className="w-full h-full text-white/50"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                      />
                    </svg>
                  </div>
                  <p className="text-white/60 text-xs md:text-sm text-center">Click or drag to upload</p>
                  {uploadError && (
                    <p className="text-red-400 text-xs mt-4 text-center max-w-md px-4">
                      {uploadError}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* PROMPT Section */}
        <div className="space-y-4">
          <h2 className="text-label font-medium text-text-subtle uppercase tracking-wider">PROMPT</h2>
          
          <div className="relative">
            <textarea
              value={prompt}
              onChange={(e) => {
                const newPrompt = e.target.value;
                setPrompt(newPrompt);
                
                // Store prompt in sessionStorage for persistence (backup)
                // This ensures prompt persists even if user signs up before generating
                if (newPrompt.trim().length >= 5) {
                  sessionStorage.setItem('pending_prompt', newPrompt.trim());
                } else {
                  sessionStorage.removeItem('pending_prompt');
                }
              }}
              placeholder="Describe your video"
              className="w-full bg-bg-panel border border-border rounded-card p-4 text-white placeholder-text-subtle focus:outline-none focus:ring-2 focus:ring-white/20 focus:border-border-hover resize-none text-body min-h-[120px]"
              rows={4}
              maxLength={500}
            />
            <div className="absolute bottom-3 right-3 flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  // Placeholder for edit/enhance functionality if needed
                }}
                className="text-text-subtle hover:text-white transition-colors"
                type="button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />
                </svg>
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setPrompt('');
                }}
                className="text-text-subtle hover:text-white transition-colors"
                type="button"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Generate Button */}
        <div className="flex flex-col items-start space-y-3">
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleGenerate();
            }}
            disabled={!canGenerate || status === 'generating'}
            className="bg-[#F9F7EA] hover:bg-[#E7D7B1] disabled:bg-gray-600 disabled:cursor-not-allowed text-[#0f1218] font-medium px-8 py-4 min-h-[52px] rounded-button transition-colors text-body flex items-center justify-center gap-2"
            type="button"
          >
            <span>{status === 'generating' ? 'Generating...' : 'Generate Video'}</span>
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
            </svg>
          </button>
          
          {/* Helper Text - Keep exactly as-is */}
          {!canGenerate && (
            <p className="text-text-muted text-body-sm">
              {!(selectedImage || imagePreview || imageAssetId) ? 'Please select an image' : ''}
              {!(selectedImage || imagePreview || imageAssetId) && prompt.trim().length < 5 ? ' and ' : ''}
              {prompt.trim().length < 5 ? 'Enter a prompt (at least 5 characters)' : ''}
            </p>
          )}
          <div className="flex items-center gap-4 text-text-muted text-body-sm">
            <span>3-5 Mins</span>
            <span className="flex items-center gap-1">
              <span>100</span>
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path d="M8.433 7.418c.155-.103.346-.196.567-.267v1.698a2.305 2.305 0 01-.567-.267C8.07 8.34 8 8.114 8 8c0-.114.07-.34.433-.582zM11 12.849v-1.698c.22.071.412.164.567.267.364.243.433.468.433.582 0 .114-.07.34-.433.582a2.305 2.305 0 01-.567.267z" />
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-13a1 1 0 10-2 0v.092a4.535 4.535 0 00-1.676.662C6.602 6.234 6 7.009 6 8c0 .99.602 1.765 1.324 2.246.48.32 1.054.545 1.676.662v1.941c-.391-.127-.68-.317-.843-.504a1 1 0 10-1.51 1.31c.562.649 1.413 1.076 2.353 1.253V15a1 1 0 102 0v-.092a4.535 4.535 0 001.676-.662C13.398 13.766 14 12.991 14 12c0-.99-.602-1.765-1.324-2.246A4.535 4.535 0 0011 9.092V7.151c.391.127.68.317.843.504a1 1 0 101.511-1.31c-.563-.649-1.413-1.076-2.354-1.253V5z"
                  clipRule="evenodd"
                />
              </svg>
              credits
            </span>
          </div>
        </div>

        {/* Generation Processing Modal */}
        {/* Show modal if generating OR if progression has started (not idle) */}
        {/* Hide modal if insufficient credits modal is showing */}
        <GenerationProcessingModal
          open={!insufficientCredits && (status === 'generating' || (progression.currentStage !== 'idle' && progression.currentStage !== 'completed' && progression.currentStage !== 'failed'))}
          progress={progress}
          progression={progression}
        />

        {/* Error Message - hide if insufficient credits modal is showing or error is about insufficient credits */}
        {error && !insufficientCredits && !error.includes('insufficient_credits') && (
          <div className="mb-8 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400">
            {error}
          </div>
        )}


        {/* Result Modal */}
        {videoUrl && (
          <GenerationResultModal
            open={showResultModal}
            videoUrl={videoUrl}
            onClose={handleResultModalClose}
            sourcePage="homepage"
          />
        )}

        {/* Actor Picker Modal - Hidden for now */}
        {/* <ActorPickerModal
          isOpen={isActorModalOpen}
          onClose={() => setIsActorModalOpen(false)}
          onSelect={handleActorSelect}
          selectedActorId={selectedActor?.id}
        /> */}
        
        {/* Insufficient Credits Modal */}
        {insufficientCredits && (
          <InsufficientCreditsModal
            current_credits={insufficientCredits.current_credits}
            required_credits={insufficientCredits.required_credits}
            message={insufficientCredits.message}
            onClose={() => setInsufficientCredits(null)}
          />
        )}
      </div>
    </div>
    </>
  );
}
